import {
  useCallback,
  useEffect,
  useState
} from 'react';

import {
  cancelStudentEventRegistration,
  listStudentHonorsEvents,
  honorsEventCategory,
  saveStudentEventRegistration
} from '../../../utils/honorsEvents';


function formatDate(
  value
) {
  if (!value) {
    return 'Date TBA';
  }

  return new Date(
    `${value}T12:00:00`
  ).toLocaleDateString(
    'en-GB',
    {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }
  );
}


function answerValue(
  answers,
  questionId
) {
  const value =
    answers?.[questionId];

  return value ===
    undefined
      ? ''
      : value;
}


function Question({
  question,
  value,
  onChange
}) {
  if (
    question.questionType ===
    'yes_no'
  ) {
    return (
      <select
        value={value}
        onChange={
          event =>
            onChange(
              event.target.value
            )
        }
      >
        <option value="">
          — Select —
        </option>

        <option value="Yes">
          Yes
        </option>

        <option value="No">
          No
        </option>
      </select>
    );
  }


  if (
    question.questionType ===
    'single_choice'
  ) {
    return (
      <select
        value={value}
        onChange={
          event =>
            onChange(
              event.target.value
            )
        }
      >
        <option value="">
          — Select —
        </option>

        {(question.options || [])
          .map(
            option => (
              <option
                key={option}
                value={option}
              >
                {option}
              </option>
            )
          )}
      </select>
    );
  }


  if (
    question.questionType ===
    'multiple_choice'
  ) {
    const selected =
      Array.isArray(value)
        ? value
        : [];

    return (
      <div className="vm-event-checkboxes">
        {(question.options || [])
          .map(
            option => (
              <label
                key={option}
              >
                <input
                  type="checkbox"
                  checked={
                    selected
                      .includes(
                        option
                      )
                  }
                  onChange={
                    event => {
                      onChange(
                        event.target
                          .checked
                          ? [
                              ...selected,
                              option
                            ]
                          : selected
                              .filter(
                                value =>
                                  value !==
                                  option
                              )
                      );
                    }
                  }
                />

                {option}
              </label>
            )
          )}
      </div>
    );
  }


  if (
    question.questionType ===
    'long_text'
  ) {
    return (
      <textarea
        rows={4}
        value={value}
        onChange={
          event =>
            onChange(
              event.target.value
            )
        }
      />
    );
  }


  return (
    <input
      type="text"
      value={value}
      onChange={
        event =>
          onChange(
            event.target.value
          )
      }
    />
  );
}


function EventRegistration({
  event,
  session,
  onSaved,
  onCancel
}) {
  const existing =
    event.registration;


  const [name, setName] =
    useState(
      existing?.name ||
      session?.englishName ||
      session?.chineseName ||
      ''
    );


  const [email, setEmail] =
    useState(
      existing?.email ||
      session?.email ||
      ''
    );


  const [answers, setAnswers] =
    useState(
      existing?.answers ||
      {}
    );


  const [guests, setGuests] =
    useState(
      existing?.guests ||
      []
    );


  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');


  const maximum =
    Number(
      event.maxGuests ||
      0
    );


  function changeAnswer(
    questionId,
    value
  ) {
    setAnswers(
      current => ({
        ...current,
        [questionId]:
          value
      })
    );
  }


  function addGuest() {
    if (
      guests.length >=
      maximum
    ) {
      return;
    }

    setGuests(
      current => [
        ...current,
        {
          name: '',
          email: '',
          answers: {}
        }
      ]
    );
  }


  function updateGuest(
    index,
    field,
    value
  ) {
    setGuests(
      current =>
        current.map(
          (
            guest,
            guestIndex
          ) =>
            guestIndex ===
              index
              ? {
                  ...guest,
                  [field]:
                    value
                }
              : guest
        )
    );
  }


  function updateGuestAnswer(
    index,
    questionId,
    value
  ) {
    setGuests(
      current =>
        current.map(
          (
            guest,
            guestIndex
          ) =>
            guestIndex ===
              index
              ? {
                  ...guest,

                  answers: {
                    ...(
                      guest.answers ||
                      {}
                    ),

                    [questionId]:
                      value
                  }
                }
              : guest
        )
    );
  }


  async function submit(
    submitEvent
  ) {
    submitEvent
      .preventDefault();

    setSaving(true);
    setError('');

    try {
      await onSaved({
        name,
        email,
        answers,
        guests
      });

    } catch (error) {
      setError(
        error?.message ||
        'Registration could not be saved.'
      );

    } finally {
      setSaving(false);
    }
  }


  return (
    <form
      className="vm-event-registration"
      onSubmit={submit}
    >
      {error && (
        <div className="vm-event-error">
          {error}
        </div>
      )}

      <h3>
        {existing?.status ===
          'Registered'
          ? 'Your registration'
          : 'Register'}
      </h3>

      <label>
        Name
      </label>

      <input
        required
        value={name}
        onChange={
          event =>
            setName(
              event.target.value
            )
        }
      />

      <label>
        Email
      </label>

      <input
        required
        type="email"
        value={email}
        onChange={
          event =>
            setEmail(
              event.target.value
            )
        }
      />


      {(event.questions || [])
        .map(
          question => (
            <div
              className="vm-event-question"
              key={
                question.questionId
              }
            >
              <label>
                {question.prompt}
                {question.required
                  ? ' *'
                  : ''}
              </label>

              <Question
                question={question}
                value={
                  answerValue(
                    answers,
                    question.questionId
                  )
                }
                onChange={
                  value =>
                    changeAnswer(
                      question
                        .questionId,
                      value
                    )
                }
              />
            </div>
          )
        )}


      {maximum > 0 && (
        <div className="vm-event-guests">
          <div className="vm-event-guest-heading">
            <h3>
              Guests
            </h3>

            <button
              type="button"
              disabled={
                guests.length >=
                maximum
              }
              onClick={
                addGuest
              }
            >
              + Add guest
            </button>
          </div>


          {guests.map(
            (
              guest,
              index
            ) => (
              <div
                className="vm-event-guest-card"
                key={index}
              >
                <div className="vm-event-guest-title">
                  Guest {index + 1}

                  <button
                    type="button"
                    onClick={() =>
                      setGuests(
                        current =>
                          current.filter(
                            (
                              _,
                              guestIndex
                            ) =>
                              guestIndex !==
                              index
                          )
                      )
                    }
                  >
                    Remove
                  </button>
                </div>

                <label>
                  Name
                </label>

                <input
                  required
                  value={
                    guest.name ||
                    ''
                  }
                  onChange={
                    event =>
                      updateGuest(
                        index,
                        'name',
                        event.target
                          .value
                      )
                  }
                />

                <label>
                  Email
                </label>

                <input
                  type="email"
                  value={
                    guest.email ||
                    ''
                  }
                  onChange={
                    event =>
                      updateGuest(
                        index,
                        'email',
                        event.target
                          .value
                      )
                  }
                />


                {(event.questions || [])
                  .filter(
                    question =>
                      question.askGuests
                  )
                  .map(
                    question => (
                      <div
                        className="vm-event-question"
                        key={
                          question.questionId
                        }
                      >
                        <label>
                          {question.prompt}
                          {question.required
                            ? ' *'
                            : ''}
                        </label>

                        <Question
                          question={
                            question
                          }
                          value={
                            answerValue(
                              guest.answers,
                              question
                                .questionId
                            )
                          }
                          onChange={
                            value =>
                              updateGuestAnswer(
                                index,
                                question
                                  .questionId,
                                value
                              )
                          }
                        />
                      </div>
                    )
                  )}
              </div>
            )
          )}
        </div>
      )}


      <button
        className="vm-event-register-button"
        disabled={saving}
      >
        {saving
          ? 'Saving…'
          : existing?.status ===
              'Registered'
            ? 'Update Registration'
            : 'Register'}
      </button>


      {existing?.status ===
        'Registered' && (
        <button
          type="button"
          className="vm-event-cancel-button"
          disabled={saving}
          onClick={
            onCancel
          }
        >
          Cancel Registration
        </button>
      )}
    </form>
  );
}


export default function MobileEvents({
  session,
  onAccessChanged,
  initialEventId = null
}) {
  const [events, setEvents] =
    useState([]);

  const [selectedId, setSelectedId] =
    useState(
      initialEventId || null
    );

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');


    const refresh =
    useCallback(
      async () => {
        const result =
          await listStudentHonorsEvents(
            session
          );

        setEvents(
          result.events || []
        );

        onAccessChanged?.(
          result.canAccessEvents ===
          true
        );

        return result;
      },
      [
        onAccessChanged,
        session
      ]
    );


  useEffect(() => {
    let active = true;

    refresh()
      .catch(error => {
        if (active) {
          setError(
            error?.message ||
            'Events could not be loaded.'
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [refresh]);


  const selected =
    events.find(
      event =>
        event.eventId ===
        selectedId
    );


  if (selected) {
    return (
      <div className="vm-events-page">
        <button
          className="vm-events-back"
          type="button"
          onClick={() =>
            setSelectedId(
              null
            )
          }
        >
          ← Events
        </button>

        {selected.posterUrl && (
          <img
            className="vm-event-poster"
            src={
              selected.posterUrl
            }
            alt=""
          />
        )}

        <div className="vm-event-detail">
          {honorsEventCategory(selected).label && (
            <span className="vm-event-kind" style={{
              color: honorsEventCategory(selected).text,
              background: honorsEventCategory(selected).background,
              borderColor: honorsEventCategory(selected).accent
            }}>
              {honorsEventCategory(selected).label}
            </span>
          )}
          <h1>
            {selected.title}
          </h1>

          <div className="vm-event-date">
            {formatDate(
              selected.eventDate
            )}

            {selected.startTime
              ? ` · ${selected.startTime}`
              : ''}

            {selected.endTime
              ? `–${selected.endTime}`
              : ''}
          </div>

          {selected.location && (
            <div className="vm-event-location">
              {selected.location}
            </div>
          )}

          {selected.description && (
            <p>
              {selected.description}
            </p>
          )}


          {(selected.speakerName ||
            selected.speakerPhotoUrl) && (
            <section className="vm-event-speaker">
              {selected.speakerPhotoUrl && (
                <img
                  src={
                    selected
                      .speakerPhotoUrl
                  }
                  alt=""
                />
              )}

              <div>
                <span>
                  Speaker
                </span>

                <strong>
                  {selected
                    .speakerName}
                </strong>

                {selected
                  .speakerDescription && (
                  <p>
                    {selected
                      .speakerDescription}
                  </p>
                )}
              </div>
            </section>
          )}


          {selected
            .registrationEnabled && (
            <EventRegistration
              event={selected}
              session={session}
              onSaved={
                async values => {
                  const result =
                    await saveStudentEventRegistration(
                      session,
                      selected
                        .eventId,
                      values
                    );

                  setEvents(
                    result.events ||
                    []
                  );
                }
              }
              onCancel={
                async () => {
                  const result =
                    await cancelStudentEventRegistration(
                      session,
                      selected
                        .eventId
                    );

                  setEvents(
                    result.events ||
                    []
                  );
                }
              }
            />
          )}
        </div>
      </div>
    );
  }


  return (
    <div className="vm-events-page">
      <div className="vm-events-heading">
        <span>
          Tunghai Honors
        </span>

        <h1>
          Events
        </h1>

        <p>
          Upcoming Honors events
          available to you.
        </p>
      </div>


      {error && (
        <div className="vm-event-error">
          {error}
        </div>
      )}


      {loading ? (
        <div className="vm-empty">
          Loading events…
        </div>

      ) : events.length ? (
        <div className="vm-event-list">
          {events.map(
            event => (
              <button
                type="button"
                style={{
                  borderLeft: '3px solid ' +
                    honorsEventCategory(event).accent
                }}
                className={
                  event.registration
                    ?.status ===
                    'Registered'
                    ? 'vm-event-card registered'
                    : 'vm-event-card'
                }
                key={
                  event.eventId
                }
                onClick={() =>
                  setSelectedId(
                    event.eventId
                  )
                }
              >
                {event.posterUrl && (
                  <img
                    src={
                      event.posterUrl
                    }
                    alt=""
                  />
                )}

                <div>
                  {honorsEventCategory(event).label && (
                    <span className="vm-event-kind" style={{
                      color: honorsEventCategory(event).text,
                      background: honorsEventCategory(event).background,
                      borderColor: honorsEventCategory(event).accent
                    }}>
                      {honorsEventCategory(event).label}
                    </span>
                  )}
                  {event.registration
                    ?.status ===
                    'Registered' && (
                    <span className="vm-event-registered">
                      ✓ Registered
                    </span>
                  )}

                  <strong>
                    {event.title}
                  </strong>

                  <span>
                    {formatDate(
                      event.eventDate
                    )}

                    {event.startTime
                      ? ` · ${event.startTime}`
                      : ''}
                  </span>

                  {event.location && (
                    <small>
                      {event.location}
                    </small>
                  )}
                </div>
              </button>
            )
          )}
        </div>

      ) : (
        <div className="vm-empty">
          No upcoming events.
        </div>
      )}
    </div>
  );
}